import React from "react";
import PolicyLayout from "../../../components/PolicyLayout";
import s from "../../../styles/PolicyLayout.module.css";

export default function PureTrackerDrinkingPrivacy() {
  return (
    <PolicyLayout
      title="privacy policy"
      appName="PureTracker: Quit Drinking"
      date="DECEMBER 2025"
    >
      <div className={s.title}>1) Introduction and Scope</div>
      <p>
        This Privacy Policy explains how “PureTracker: Quit Drinking” (the “App”) collects,
        uses, stores, and discloses information when you track your habit-free
        streaks, record journal entries and moods, get motivational content, and
        optionally chat with an AI Coach. The App exists in two public
        variants—PureTracker: Quit Drinking and PureTracker: Quit Vaping—which
        share one codebase and the same data practices while differing in
        visuals and text. This Policy covers onboarding and optional sign-in,
        progress and savings estimators, achievements, notifications,
        subscriptions and entitlements, analytics and crash diagnostics, and an
        AI Coach provided through a secure server proxy. Core features are
        designed to work offline with on-device storage; when you opt into sync,
        selected data may be stored in Firebase services under your account. By
        using the App, you acknowledge this Policy and agree to these practices.
        If you disagree, you should uninstall the App and, where applicable,
        request deletion of account-linked records as described below.
      </p>

      <div className={s.title}>2) Controller and Contact Information</div>
      <p>
        The data controller responsible for determining the purposes and means
        of processing is Aleksandra Rakitina. If you have questions about
        privacy or wish to exercise data rights such as access, deletion,
        correction, restriction, objection, or portability, contact
        alexwadek@gmail.com. To prevent unauthorized access or deletion, we
        verify identity before acting on requests—typically by confirming
        control over the sign-in email, or by asking for limited additional
        information that we promptly discard after verification. We may retain
        privacy correspondence where permitted by law to document compliance,
        resolve disputes, and demonstrate our response timelines. If your
        request concerns platform subscription charges or refunds, we will
        direct you to your device account settings while continuing to address
        privacy matters that fall within our control.
      </p>

      <div
        className={s.title}
      >{`3) Key Principles (Supportive, Non-Medical; Minimization; User Control)`}</div>
      <p>
        PureTracker’s purpose is supportive self-reflection and motivation. It
        is not a medical or cessation program, and it does not provide
        diagnosis, treatment, or professional advice. We apply data
        minimization by collecting only what is necessary for the App to
        function: authentication and sync (optional), entitlements for Premium,
        stability analytics and crash diagnostics, notifications you opt into,
        and content delivery. We emphasize user control: you decide whether to
        sign in or remain a guest, what to write in your journal, whether to use
        AI Coach, and who sees content you choose to share. We do not sell
        Personal Data. We avoid behavioral advertising SDKs and keep identifiers
        scoped to security, entitlements, and reliability.
      </p>

      <div className={s.title}>4) Definitions</div>
      <p>
        “Personal Data” means information that identifies or can be reasonably
        linked to an individual. “Progress Data” includes habit type selected
        (drinking or vaping), streak start/reset date, derived days free,
        estimated savings based on your inputs, and milestones reached. “Journal
        Data” refers to notes you write, mood indicators you select, and
        timestamps. “Motivation Data” includes quotes and cards you view, like,
        or save for later. “Entitlement Data” means subscription status and
        eligibility received from our billing provider (RevenueCat). “Content
        Config” means localized strings, visual sets, and flags delivered from
        assets or Firebase Remote Config/Firestore. “Processing” includes
        collecting, storing, using, transforming, transmitting, sharing, and
        deleting data as needed to provide features and comply with law.
      </p>

      <div className={s.title}>5) What We Collect (Categories of Data)</div>
      <p>
        Depending on your choices, permissions, and device settings, we may
        process: (a) Account data via Firebase Auth if you sign in (email,
        provider identifiers, display name if provided) or a local guest
        identifier if you remain anonymous; (b) Progress Data including habit
        type, streak counters, your savings assumptions, and computed savings;
        (c) Journal Data such as mood (e.g., emoji scale) and short reflections
        you enter; (d) Motivation Data including items you like or save; (e)
        Content Config and remote feature flags from Firebase; (f) Entitlement
        Data from RevenueCat (product IDs, trial eligibility, renewal outcomes,
        expiration/cancellation dates) so we can grant Premium features like
        unlimited AI Coach and full motivation access; (g) Technical, analytics,
        and crash data such as app/OS version, device model, anonymized error
        codes, and event counters; and (h) Notification tokens and preferences
        required to route push messages you opt into. We do not receive or store
        your payment card numbers, which are handled by platform billing.
      </p>

      <div className={s.title}>6) Sources of Data and Voluntary Provision</div>
      <p>
        Most information originates from you when you set your habit type, start
        or reset a streak, input spending assumptions for savings estimates,
        save or like motivational items, and write journal entries. Technical
        and diagnostic data arises automatically from the app runtime and
        service providers to maintain reliability and address performance
        issues. Content may be delivered from local assets or fetched from
        Firebase when you are online. You are not required to provide journal
        text or sign in to use core functionality; however, certain
        features—like cross-device restoration, extended insights, and Premium
        entitlements—require network connectivity and limited account-level
        data.
      </p>

      <div className={s.title}>7) Offline-First Storage and Optional Sync</div>
      <p>
        The App is designed to function offline using on-device storage
        (Hive/SharedPreferences). Your Progress Data, Journal Data, and
        Motivation Data are written locally so you can view streaks, jot down
        reflections, and browse saved quotes without an active connection. When
        you sign in and connectivity exists, selected records may synchronize to
        Firebase Firestore to enable restoration across devices and protection
        against device loss. If you prefer not to sign in, your information
        remains on-device; note that deleting the App or resetting your device
        may permanently remove guest data. You can link a guest profile later to
        back up data under an authenticated account.
      </p>

      <div className={s.title}>8) Streaks, Savings, and Derived Insights</div>
      <p>
        Streak counters are computed on-device using your selected habit type
        and the most recent reset or start date. Savings are estimates based on
        the daily amount you enter and may include friendly, non-medical
        “benefit” summaries described in general terms only. These figures are
        not medical or financial advice and may differ from your real outcomes.
        You can edit your assumptions in Settings at any time; new values apply
        going forward and do not change previously stored entries unless you
        request a recalculation. We may show lightweight charts or badges
        derived from your data; these artifacts reside locally and, if you opt
        into sync, can be stored with your account.
      </p>

      <div className={s.title}>9) Motivation Feed and Saved Items</div>
      <p>
        Motivational content appears as quote or fact cards and imagery curated
        for encouragement. Content is either bundled with the App or delivered
        from Firebase. You can like, save, or hide items; those actions update
        your local profile and, if you are signed in, may sync to Firestore for
        multi-device continuity. Some content packs may be Premium-only. We
        avoid embedding external trackers in this content and do not sell
        viewing behavior to advertising networks. You remain responsible for
        what you choose to share outside the App via screenshots or exports.
      </p>

      <div className={s.title}>10) Journal and Mood Tracker</div>
      <p>
        The journal lets you record short reflections and assign a mood
        indicator (e.g., 1–5). Entries are stored locally by default; if you
        sign in and choose to sync, they may be stored in Firestore. You can
        edit or delete entries at any time, and in Settings you can clear
        journal history on the device. We recommend avoiding sensitive medical
        details, financial information, or other highly personal data you do not
        wish to store digitally. If you share text externally (messages, email,
        social apps), those services’ privacy practices apply, and deleting the
        entry in the App does not remove external copies.
      </p>

      <div className={s.title}>
        11) Optional AI Coach (Secure Proxy and Minimal Input)
      </div>
      <p>
        If you open the AI Coach, your prompt and minimal context are sent to a
        secured serverless proxy (e.g., Firebase Functions) that uses our
        backend API key to call an AI model provider over HTTPS. We do not embed
        advertising identifiers in AI requests, and we do not allow the provider
        to use your prompts for independent marketing. The proxy may log limited
        technical metadata—timestamps, request size, latency, and status
        codes—for reliability and abuse prevention, retaining these records only
        as long as operationally necessary. You can keep AI entirely off by not
        opening the chat. Premium removes daily limits but does not broaden data
        sharing beyond what is required to answer your prompts.
      </p>

      <div className={s.title}>
        12) Subscriptions, Trials, Pricing, and Entitlements
      </div>
      <p>
        Premium subscriptions use platform billing exposed through RevenueCat.
        Plans may include weekly $4.99 (3-day trial), monthly $14.99, yearly
        $39.99. We receive limited Entitlement Data (product IDs, trial
        eligibility, renewal outcomes, expiration/cancellation) to unlock
        Premium features such as unlimited AI Coach, the full motivation
        library, extended insights/history, and an ad-free experience. We do not
        receive payment card numbers. Manage or cancel in your device account
        settings; grace periods, refunds, territory taxes, and price changes
        follow platform rules and applicable law. Entitlements are refreshed
        periodically; online access is needed occasionally to validate Premium.
      </p>

      <div className={s.title}>13) Notifications and Local Reminders</div>
      <p>
        With your permission, the App may send push notifications such as
        milestone reminders (“7 days—keep going!”), daily encouragement, or
        weekly summaries. Delivery requires a device token assigned by the
        platform messaging service; we use this token solely to route messages
        to your device and avoid placing sensitive journal text in notification
        bodies. You can disable notifications in system settings and, where
        offered, in-app toggles. Uninstalling the App typically invalidates
        tokens, although short propagation delays may occur before revocation is
        complete.
      </p>

      <div className={s.title}>
        14) Analytics, Events, and Crash Diagnostics
      </div>
      <p>
        We use Firebase Analytics and Crashlytics to ensure stability, measure
        feature adoption, and improve usability—not to create third-party
        advertising profiles. Typical events include app_open, start_trial,
        purchase, ai_message count, journal_add, plus view or click counters on
        major screens and anonymized error codes and device/OS versions. Where
        feasible, analytics are aggregated or pseudonymized, and we avoid
        logging journal text or full AI conversations. You can reduce analytics
        via platform privacy controls; doing so may limit our ability to
        diagnose device-specific issues after updates.
      </p>

      <div className={s.title}>15) What We Do Not Collect or Sell</div>
      <p>
        We do not sell Personal Data, and we do not buy data about you from data
        brokers. We do not collect payment card numbers, passport or national
        IDs, precise GPS location, or your phone contacts for core features. We
        do not embed invisible tracking beacons in exported images or shared
        text. We do not share your Journal Data or AI prompts with advertising
        networks. We do not claim clinical efficacy and do not collect clinical
        records.
      </p>

      <div className={s.title}>
        16) Legal Bases (EEA/UK) and Global Principles
      </div>
      <p>
        For users in the EEA/UK, our legal bases include: performance of a
        contract (core features such as streak tracking, sync if enabled, and
        Premium entitlements); legitimate interests (security, fraud prevention,
        service improvement) balanced against your rights through minimization
        and opt-outs; consent (notifications, certain analytics categories, and
        AI Coach where required); and legal obligations (recordkeeping and
        lawful requests). If we materially change purposes or expand data use
        beyond what is compatible with the original reasons, we will update this
        Policy and seek consent where required by law.
      </p>

      <div className={s.title}>17) Security Measures and Architecture</div>
      <p>
        We implement layered safeguards appropriate to the risks involved.
        Communications with providers use HTTPS and certificate validation;
        Firestore and Cloud Storage encrypt data at rest; access to operational
        tools follows least-privilege principles and is logged; environments are
        separated for development and production; credentials are rotated;
        dependencies are monitored for known vulnerabilities. The offline-first
        architecture reduces exposure because most content—streaks, journals,
        and saved items—resides on your device unless you choose to sync or
        share. No method is perfectly secure, but we promptly investigate
        incidents and remediate issues when discovered.
      </p>

      <div className={s.title}>18) Data Retention and Deletion</div>
      <p>
        Local data persists on your device until you delete it or uninstall the
        App. If you use sync, records in Firestore remain until you delete them
        in-app or request account deletion; provider backups and replication can
        introduce short delays before all copies are purged. Entitlement Data
        may be retained longer as required for accounting and fraud prevention,
        typically in pseudonymized or aggregated form. Crash logs and analytics
        are preserved for operational time windows to analyze trends and then
        are de-identified or deleted. Verified deletion requests are processed
        within a reasonable timeframe consistent with applicable law.
      </p>

      <div className={s.title}>19) International Data Transfers</div>
      <p>
        Depending on your location and the locations of our service providers,
        your data may be processed in countries with different data-protection
        rules. Where required, we rely on recognized safeguards—such as standard
        contractual clauses and platform transfer mechanisms—to help protect
        Personal Data in cross-border contexts. Regardless of location,
        providers are contractually obligated to implement appropriate security
        and to process data only on our instructions. You may contact us for
        information about relevant transfer mechanisms that do not reveal
        confidential commercial details.
      </p>

      <div className={s.title}>20) Your Rights and How to Exercise Them</div>
      <p>
        Subject to law, you may have rights to access, rectify, delete, or port
        Personal Data; to restrict or object to certain processing; and to
        withdraw consent where processing relies on consent. In the App, you can
        clear local history, delete journal entries, adjust savings assumptions,
        disable notifications, and choose whether to sign in or remain a guest.
        Subscription management (including cancellation and refunds) occurs in
        your device account settings. For other rights, email
        alexwadek@gmail.com from your sign-in address (if applicable); we will
        verify identity and respond within a reasonable period. If we cannot
        fulfill a request due to legal or technical limits, we will explain
        unless prohibited.
      </p>

      <div className={s.title}>
        21) Children’s Privacy and Sensitive Uses
      </div>
      <p>
        The App is intended for adults. We do not knowingly collect Personal
        Data from users under the age where parental consent is legally
        required. We avoid glamorizing alcohol or nicotine and emphasize
        encouragement for users seeking to change habits. If you believe a minor
        used the App without appropriate permission, contact alexwadek@gmail.com
        so we can remove information and restrict access as appropriate. Parents
        and guardians can also use device controls to limit in-app purchases and
        notifications at the OS level.
      </p>

      <div className={s.title}>
        22) Sharing, Exports, and External Destinations
      </div>
      <p>
        If you choose to share a streak card, achievement badge, or text
        summary, the content is passed to the system share sheet and delivered
        through your selected destination app (messages, email, social). Those
        services operate under their own privacy policies, which we do not
        control. Deleting an item within PureTracker does not delete copies you
        may have exported or that recipients have saved. Before sharing, consider
        the audience, visibility, and settings of the destination platform,
        especially if the content reveals sensitive personal choices or goals.
      </p>

      <div className={s.title}>
        23) Remote Config, Experiments, and Fair Use Limits
      </div>
      <p>
        We may use Firebase Remote Config to toggle non-personalized features
        (e.g., copy on paywalls, content pack availability, trial banners) and
        to run experiments that compare alternative presentations. These tests
        use minimal identifiers and aggregated metrics to evaluate performance
        and do not require storing journal text or AI messages for marketing. To
        maintain reliability and cost control, we may apply fair-use safeguards
        such as rate limits on AI prompts or content fetches—especially during
        abuse spikes—without affecting normal usage for typical users.
      </p>

      <div className={s.title}>
        24) Third-Party Providers and Disclosures
      </div>
      <p>
        We rely on vetted providers acting on our instructions: Firebase (Auth
        optional, Firestore, Storage, Remote Config, Analytics, Messaging,
        Crashlytics, Functions), RevenueCat (subscriptions and entitlements),
        and an AI model provider accessed via our secure proxy. Providers are
        bound by terms requiring appropriate security and prohibiting the use of
        Personal Data for their independent marketing. We may disclose
        information when legally required by competent authorities after
        validating scope and jurisdiction, or in a business transition where a
        successor honors this Policy or provides clear notice of material
        changes and choices.
      </p>

      <div className={s.title}>
        25) Accuracy, Limitations, and Health Disclaimer
      </div>
      <p>
        PureTracker offers motivation and self-reflection—not medical care.
        Streaks, savings, and benefits are estimates based on your inputs and
        general informational content; outcomes vary by person and context. The
        AI Coach produces generated text that may be incomplete or inaccurate
        and should be viewed as supportive guidance rather than professional
        advice. If you need medical or therapeutic assistance, consult qualified
        professionals. Using the App does not create a patient-provider
        relationship, and no content should be relied upon for diagnosis or
        treatment decisions.
      </p>

      <div className={s.title}>26) Changes to This Policy</div>
      <p>
        We may revise this Policy to reflect operational, legal, or regulatory
        developments. The “Last Updated” date will change when updates occur. If
        a change materially affects your rights or our data uses, we will
        highlight the update in-app and, where required, request consent before
        applying new purposes. Continued use of the App after changes take
        effect signifies acceptance of the updated Policy. If you disagree with
        updates, you may stop using the App and request deletion of any
        account-linked records we control, subject to identity verification and
        applicable retention obligations.
      </p>

      <div className={s.title}>
        27) How to Submit Privacy Requests and Verification Steps
      </div>
      <p>
        To exercise your rights, email alexwadek@gmail.com and specify your
        request type (access, deletion, correction, restriction, objection,
        portability). Include helpful context such as device model, app version,
        and whether you use a signed-in account or guest mode. We will verify
        identity (for example, by confirming control of the account email)
        before acting. Where feasible, data will be provided in a portable,
        machine-readable format. We may decline or limit requests when permitted
        by law—for example, if fulfilling a request would compromise security,
        interfere with legal obligations, or infringe on the privacy rights of
        others—and we will explain our reasoning unless prohibited.
      </p>
    </PolicyLayout>
  );
}
